import React, { useEffect, useMemo, useState } from "react";
import { EmailListItem, EmailModel, TagRefModel, TagValueModel, TagOption, MediaModel } from "../utils/Types";
import { useApiConfig } from "../utils/ApiConfigContext.tsx";
import { useAuth } from "../utils/AuthContext.tsx";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import FileDownloadButton from "./FileDownloadButton";
import TagCloud from "./TagCloud.tsx";
import { ContactLabel } from "./ContactLabel.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEmail, useMessageMedia, useMessageTags, useCreateMessageTag, useDeleteMessageTag } from "@/utils/Api.ts";

interface EmailProps {
  emailId: string | undefined;
  onLoad: (selectedEmail: EmailModel | null) => void;
  preload?: EmailListItem | null;
}

const EmailBody: React.FC<EmailProps> = ({ emailId, onLoad, preload }) => {
  const { apiUrls } = useApiConfig();
  const { token } = useAuth();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState<boolean>(false);
  const [text, setText] = useState<string[] | null>(null);
  const [referenced, setReferenced] = useState<string[] | null>(null);
  const [showReferenced, setShowReferenced] = useState<boolean>(false);

  const options: TagOption[] = [
    { name: "status", color: "bg-lime-200 text-gray-800", label: "Status", lock: false, icon: "ChartGantt" },
    { name: "project", color: "bg-slate-500 text-gray-100", label: "Project", lock: true, icon: "FolderClosed" },
  ];

  const { data: email, isFetching } = useEmail(emailId, token ?? undefined);
  const { data: values, isFetching: isFetchingTagValues } = useMessageTags(emailId, token ?? undefined);
  const { data: attachments } = useMessageMedia(emailId, token ?? undefined);
  const { mutate: addMessageTag } = useCreateMessageTag(token ?? undefined);
  const { mutate: removeContactTag } = useDeleteMessageTag(token ?? undefined);

  useMemo(() => {
    setText(null);
    setReferenced(null);
    setShowReferenced(false);

    if (email) {
      const lines = email.text.split("\n");
      setText(lines);

      if (email.trimmed) {
        const trimmedLines = email.trimmed.split("\n");
        if (trimmedLines.length != lines.length) {
          setText(trimmedLines);
          setReferenced(lines.slice(trimmedLines.length - 1));
        }
      }
      if (onLoad) onLoad(email);
    }
  }, [email]);

  //omits inline attachments that are embedded in email body
  const filteredAttachments = useMemo<MediaModel[]>(() => {
    if (attachments) return attachments.filter((a) => a.contentDisposition !== "inline");
    return [];
  }, [attachments]);

  useEffect(() => {
    setOpen(emailId != null);
  }, [emailId]);

  const handleToggleShowReferenced = () => {
    setShowReferenced(!showReferenced);
  };

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
  };

  const addTag = async (value: TagValueModel) => {
    addMessageTag({ value: { id: "", path: value.path, source: "user" }, messageId: emailId! });
  };

  const removeTag = async (ref: TagRefModel) => {
    removeContactTag({ tagId: ref.id, messageId: emailId! });
  };

  const getSubject = () => {
    if (isFetching) {
      if (preload) return preload.subject;
      return <Skeleton className="h-4 w-1/2" />;
    }
    return email?.subject;
  };

  return (
    <>
      {
        <Sheet
          open={isMobile ? open : emailId != undefined}
          modal={isMobile ? true : false}
          onOpenChange={handleOpenChange}
        >
          <SheetContent
            className="py-2 h-[80vh] shadow-none overflow-y-scroll overflow-x-clip sm:h-[100vh] lg:inline lg:px-8 lg:[&>button]:hidden xl:max-w-[640px] lg:max-w-[460px] xl:pl-12"
            side={isMobile ? "bottom" : "right"}
          >
            <SheetHeader>
              <SheetTitle className="flex flex-col justify-start">
                <div className="scroll-m-20 text-xl font-semibold tracking-tight text-left pt-8 pb-4 min-h-8 text-accent-foreground">
                  {getSubject()}
                </div>
                <div className="pt-4 pb-12">
                  <TagCloud
                    options={options}
                    prompt="Tags"
                    selections={values}
                    onTagAdded={(value: TagValueModel) => addTag(value)}
                    onTagRemoved={(value: TagRefModel) => removeTag(value)}
                    loading={isFetchingTagValues}
                  />
                </div>
              </SheetTitle>
            </SheetHeader>

            <div className="text-left">
              {!isFetching && email && (
                <>
                  <div>
                    From:{" "}
                    <ContactLabel
                      displayName={email.from.name || email.from.address}
                      identity={email.from.address.toLowerCase()}
                    />
                  </div>
                  {email.to.length > 0 && (
                    <ul className="flex flex-wrap gap-x-1">
                      <li>To:</li>
                      {email.to.map((item, index) => {
                        return (
                          <li key={index} className="after:content-[','] last:after:content-['']">
                            <ContactLabel
                              displayName={item.name || item.address}
                              identity={item.address.toLowerCase()}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {email.cc?.length > 0 && (
                    <ul className="flex flex-wrap gap-x-1">
                      <li>Cc:</li>
                      {email.cc.map((item, index) => {
                        return (
                          <li key={index} className="after:content-[','] last:after:content-['']">
                            <ContactLabel
                              displayName={item.name || item.address}
                              identity={item.address.toLowerCase()}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {email.bcc?.length > 0 && (
                    <ul className="flex flex-wrap gap-x-1">
                      <li>Bcc:</li>
                      {email.bcc.map((item, index) => {
                        return (
                          <li key={index} className="after:content-[','] last:after:content-['']">
                            <ContactLabel
                              displayName={item.name || item.address}
                              identity={item.address.toLowerCase()}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
              {isFetching && <Skeleton className="h-4 w-1/4" />}
            </div>
            <div className="py-8">
              {!isFetching &&
                email &&
                text?.map((line, index) => (
                  <div key={index}>
                    {line}
                    <br />
                  </div>
                ))}
              <div className="text-base flex flex-wrap gap-2">
                <ul className="space-y-2 pb-2">
                  {!isFetching &&
                    email &&
                    filteredAttachments.map((media, i) => {
                      return (
                        <li key={i}>
                          <FileDownloadButton
                            url={apiUrls.getMessageMediaById(email.id, media.id)}
                            filename={media.title}
                            key={i}
                          />
                        </li>
                      );
                    })}
                  {isFetching && (
                    <>
                      <li key={1}>
                        <Skeleton className="h-6 w-16" />
                      </li>
                      <li key={2}>
                        <Skeleton className="h-6 w-16" />
                      </li>
                    </>
                  )}
                </ul>
              </div>
              {!isFetching && email && referenced && (
                <div className="border-t border-dashed mt-4">
                  <div className="flex justify-end py-2">
                    <Button
                      onClick={handleToggleShowReferenced}
                      variant="link"
                      size="sm"
                      className="text-muted-foreground"
                    >
                      {!showReferenced && (
                        <>
                          Message Trimmed - Show Full Message
                          <ChevronDown />
                        </>
                      )}
                      {showReferenced && (
                        <>
                          Hide Full Message
                          <ChevronUp />
                        </>
                      )}
                    </Button>
                  </div>

                  {showReferenced && (
                    <div className="bg-muted text-muted-foreground rounded-lg p-4 mb-2">
                      {referenced?.map((line, index) => (
                        <div key={index}>
                          {line}
                          <br />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {isFetching && (
                <div className="flex flex-col gap-4 pt-8">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      }
    </>
  );
};

export default EmailBody;
